import { useQuery } from "@apollo/client";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
  Legend,
} from "recharts";
import Card from "components/base/Card";
import Spinner from "components/base/Spinner";
import Errors from "components/base/Errors";
import { useModals } from "ModalProvider";
import ShipmentsView from "./ShipmentsView";
import { SHIPMENT_ANALYTICS_QUARTER_REPORT } from "./graphql";
import ShipmentErrorsView from "./ShipmentErrorsView";
import { useEffect, useState } from "react";
import DateRangeSlider from "components/base/DateRangeSlider";
import { Select } from "components/base/Form";
import DateRangePicker from "pages/analytics/DateRangePicker";
import { useDateRangeFrom } from "hooks/useDateRange";
import { formatDate } from "react-day-picker/moment";

function validAndHasStartProduction(shipment) {
  return (
    shipment.shipmentStatus.filter((i) => i.status === "in production").length >
      0 &&
    shipment.shipmentStatus.filter(
      (i) => ["canceled", "old", "-"].indexOf(i.status) > -1,
    ).length === 0
  );
}

function formatRateValue(value) {
  const rate = Number(value);
  if (!Number.isFinite(rate)) return value;
  const roundedRate = Number.isInteger(rate)
    ? String(rate)
    : rate.toFixed(2).replace(/\.?0+$/, "");
  return `${roundedRate}%`;
}

function ReportPage() {
  const [group, setGroup] = useState("Q");
  const [{ start, end }, { setStart, setEnd }] = useDateRangeFrom(
    new Date(2022, 0, 1),
  );

  const { loading, error, data, refetch } = useQuery(
    SHIPMENT_ANALYTICS_QUARTER_REPORT,
    {
      variables: {
        group: group,
        startDate: formatDate(start, "YYYY-MM-DD"),
        endDate: formatDate(end, "YYYY-MM-DD"),
      },
    },
  );
  const modal = useModals();

  if (loading) return <Spinner />;
  if (error) return <Errors />;

  let shipmentsInProductionErrorsDelays = [];
  let shipments = data.shipmentList
    .filter(validAndHasStartProduction)
    .map((shipment) => {
      const deliveredToPortStatus = shipment.shipmentStatus.find(
        (i) => i.status === "delivered to port",
      );
      return {
        ...shipment,
        productionStart: shipment.shipmentStatus.find(
          (i) => i.status === "in production",
        ).created,
        productionEnd: deliveredToPortStatus
          ? deliveredToPortStatus.created
          : null,
      };
    });

  let countedShipments = [];
  data.shipmentQuarters.forEach((quarter) => {
    let quarterData = { ...quarter };
    delete quarterData.__typename;

    let quarterStartDate = new Date(quarter.year, (quarter.quarter - 1) * 3);
    let quarterEndDate = new Date(
      quarter.year,
      quarterStartDate.getMonth() + 3,
      0,
    );

    quarterStartDate = quarterStartDate.getTime() / 1000;
    quarterEndDate = quarterEndDate.getTime() / 1000;

    let ordersCount = 0;

    for (let i = 0; i < shipments.length; i++) {
      let shipment = shipments[i];
      if (shipment.productionStart < quarterEndDate) {
        if (
          shipment.productionEnd === null ||
          shipment.productionEnd >= quarterStartDate
        ) {
          ordersCount += 1;
          countedShipments.push(shipment.id);
        }
      }
    }

    let errorsForThisQuarter = data.shipmentErrorsByQuarter.find(
      (i) => i.year === quarter.year && i.quarter === quarter.quarter,
    );

    let errorsWithDelayForThisQuarter =
      data.shipmentErrorsWithDelayByQuarter.find(
        (i) => i.year === quarter.year && i.quarter === quarter.quarter,
      );

    quarterData = {
      year: quarter.year,
      quarter: quarter.quarter,
      ordersCount,
      errorCount: errorsForThisQuarter ? errorsForThisQuarter.value : 0,
      delayCount: errorsWithDelayForThisQuarter
        ? errorsWithDelayForThisQuarter.errorCount
        : 0,
    };
    shipmentsInProductionErrorsDelays.push(quarterData);
  });

  const shipmentGoals = JSON.parse(data.shipmentGoals);
  const shipmentGoalsDelayDays = shipmentGoals.delay_days || [];
  const shipmentGoalsProductionDays = shipmentGoals.production_days || [];
  const shipmentGoalsOnTimeDelivery = shipmentGoals.on_time_delivery || [];
  const shipmentGoalsContainerLoad = shipmentGoals.container_load || [];
  const shipmentGoalsShipments = shipmentGoals.shipments || [];

  function onClickAveDelayDaysBarHandler(e) {
    modal.present({
      title: e.name,
      subtitle: `Average Delay Days: ${e.value}`,
      subtitleRight: null,
      children: <ShipmentsView shipments={e.shipments} type="AVE_DELAY_DAYS" />,
    });
  }

  function onClickOnTimeDeliveryBarHandler(e) {
    modal.present({
      title: e.name,
      subtitle: "On Time Delivery",
      subtitleRight: formatRateValue(e.value),
      children: (
        <ShipmentsView shipments={e.shipments} type="ON_TIME_DELIVERY" />
      ),
    });
  }

  function onClickFullContainerLoadBarHandler(e) {
    modal.present({
      title: e.name,
      subtitle: "Full Container Load",
      subtitleRight: formatRateValue(e.value),
      children: (
        <ShipmentsView shipments={e.shipments} type="FULL_CONTAINER_LOAD" />
      ),
    });
  }

  function presentProductionDaysModal(e) {
    modal.present({
      title: e.name,
      subtitle: `Average Production Days: ${e.value}`,
      subtitleRight: null,
      children: (
        <ShipmentsView
          shipments={e.shipments}
          type="AVE_PRODUCTION_DAYS"
          onTrackLeadtimeChange={async () => {
            const { data: refreshedData } = await refetch();
            const refreshedGroup = JSON.parse(
              refreshedData.shipmentGoals,
            ).production_days.find((item) => item.group === e.name);

            if (!refreshedGroup) return;

            presentProductionDaysModal({
              ...refreshedGroup,
              name: refreshedGroup.group,
              value: refreshedGroup.ave_production_days,
            });
          }}
        />
      ),
    });
  }

  function onClickAveProductionDaysBarHandler(e) {
    presentProductionDaysModal(e);
  }

  function onClickDeliveredToPortBarHandler(e) {
    modal.present({
      title: e.name,
      subtitle: `Delivered to Port: ${e.value} shipments`,
      subtitleRight: null,
      children: (
        <ShipmentsView shipments={e.shipments} type="DELIVERED_TO_PORT" />
      ),
    });
  }

  return (
    <div>
      <div className="p-6">
        <div className="mb-4 flex space-x-4 items-center">
          <DateRangePicker
            start={start}
            end={end}
            setStart={setStart}
            setEnd={setEnd}
          />

          <Select value={group} onChange={(e) => setGroup(e.target.value)}>
            <option value="M">by Month</option>
            <option value="Q">by Quarter</option>
            <option value="Y">by Year</option>
          </Select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-4 lg:gap-x-6 lg:gap-y-6">
          <ReportChart
            title="Average Delay Days"
            subtitle="Shipments are grouped base on their `delivered to port` date."
            data={shipmentGoalsDelayDays.map((i) => ({
              ...i,
              name: i.group,
              value: i.ave_delay_days,
            }))}
            fillColor="#f687b3"
            valueName="Days"
            onClick={onClickAveDelayDaysBarHandler}
          />

          <ReportChart
            title="Average Production Days"
            subtitle="Shipments are grouped base on their `delivered to port` date."
            data={shipmentGoalsProductionDays.map((i) => ({
              ...i,
              name: i.group,
              value: i.ave_production_days,
            }))}
            fillColor="#68D391"
            valueName="Days"
            onClick={onClickAveProductionDaysBarHandler}
          />

          <ReportChart
            title="On Time Delivery Rate"
            subtitle="Shipments are grouped base on their `delivered to port` date."
            data={shipmentGoalsOnTimeDelivery.map((i) => ({
              ...i,
              name: i.group,
              value: i.rate,
            }))}
            fillColor="#3b82f6"
            valueName="Rate %"
            onClick={onClickOnTimeDeliveryBarHandler}
          />

          <ReportChart
            title="Full Container Load"
            subtitle="Shipments are grouped base on their `delivered to port` date."
            data={shipmentGoalsContainerLoad.map((i) => ({
              ...i,
              name: i.group,
              value: i.avg_full_container_load,
            }))}
            fillColor="#84cc16"
            valueName="Rate %"
            onClick={onClickFullContainerLoadBarHandler}
          />

          <ReportChart
            width="w-full"
            title="Delivered to Port"
            subtitle="Shipments are grouped base on their `delivered to port` date."
            data={shipmentGoalsShipments.map((i) => ({
              ...i,
              name: i.group,
              value: i.total_shipments,
            }))}
            fillColor="#9333ea"
            valueName="Shipments"
            onClick={onClickDeliveredToPortBarHandler}
          />

        </div>

        <div className="mt-4">
          {/* a problem with the statistical results, should be grouped according to "delivered to port" status creation time.  */}
          <QuarterCombinedReport
            title="Orders in Production, Errors and Delays"
            data={shipmentsInProductionErrorsDelays}
          />
        </div>
      </div>

      <div className="p-6 relative">
        <ShipmentErrorsView />
      </div>
    </div>
  );
}

const QuarterCombinedReport = ({ title, data }) => {
  const [dateRange, setDateRange] = useState([0, data.length - 1]);

  const chartData = data.map((i) => ({
    ...i,
    name: `${i.year} - Q${i.quarter}`,
  }));

  const filteredData = chartData.slice(dateRange[0], dateRange[1] + 1);

  return (
    <div className="w-full">
      <Card title={title}>
        <ResponsiveContainer className="mt-8" width="100%" height={200}>
          <BarChart
            data={filteredData}
            stackOffset="silhouette"
            margin={{ top: 20, right: 5, bottom: 0, left: 0 }}
          >
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip cursor={{ fill: "#f5f5f5" }} />
            <Legend />
            <Bar dataKey="ordersCount" fill="#4299E1" name="Orders">
              <LabelList dataKey="ordersCount" position="top" />
            </Bar>
            <Bar dataKey="errorCount" fill="#F6AD55" name="Errors">
              <LabelList dataKey="errorCount" position="top" />
            </Bar>
            <Bar dataKey="delayCount" fill="#FC8181" name="Delay">
              <LabelList dataKey="delayCount" position="top" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        <DateRangeSlider
          min={0}
          max={data.length - 1}
          onChange={setDateRange}
        />
      </Card>
    </div>
  );
};

const ReportChart = ({
  title,
  subtitle,
  valueName,
  data,
  fillColor,
  width = "w-full",
  onClick,
  className,
}) => {
  const [dateRange, setDateRange] = useState([0, data.length - 1]);
  const [activeAnimation, setActiveAnimation] = useState(true);

  useEffect(() => {
    setDateRange([0, data.length - 1]);
  }, [data]);

  useEffect(() => {
    // Only animate the first time
    if (activeAnimation) {
      setTimeout(() => {
        setActiveAnimation(false);
      }, 500);
    }
  }, []);

  const filteredData = data.slice(dateRange[0], dateRange[1] + 1);

  return (
    <div className={`${width} ${className}`}>
      <Card title={title} subtitle={subtitle}>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart
            data={filteredData}
            stackOffset="silhouette"
            margin={{ top: 20, right: 0, bottom: 0, left: -30 }}
          >
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip cursor={{ fill: "#f5f5f5" }} />
            <Bar
              key={Date.now()}
              dataKey="value"
              maxBarSize={40}
              minBarSize={10}
              fill={fillColor}
              name={valueName}
              onClick={onClick ? onClick : null}
              className={onClick ? "cursor-pointer" : ""}
              isAnimationActive={activeAnimation}
            >
              <LabelList dataKey="value" position="top" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <DateRangeSlider
          key={data.length}
          min={0}
          max={data.length - 1}
          onChange={setDateRange}
        />
      </Card>
    </div>
  );
};

export default ReportPage;
